import React, { useEffect, useState } from "react";
import api from "../utils/api";
import axios from 'axios';
import DetailModal from '../components/DetailModal';
import './Bookings.css';

interface Booking {
  id: number;
  status: string;
  hotel: string;
  amount: number;
  created: string;
  type?: 'flight' | 'hotel' | 'other';
  pnr?: string | null;
  ref?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  destination?: string;
  origin?: string;
  nights?: number;
  checkIn?: string;
  checkOut?: string;
  flightNumber?: string;
  airline?: string;
  roomType?: string;
  guests?: number;
}

const Bookings: React.FC = () => {
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [filteredBookings, setFilteredBookings] = useState<Booking[] | null>(null);
  const [invoiceMap, setInvoiceMap] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created');
  const [dateRange, setDateRange] = useState('all');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<any | null>(null);
  const [modalBooking, setModalBooking] = useState<Booking | null>(null);
  const [modalTitle, setModalTitle] = useState('');

  useEffect(() => {
    loadBookings();
  }, []);

  useEffect(() => {
    if (bookings) {
      filterAndSortBookings();
    }
  }, [bookings, searchTerm, statusFilter, typeFilter, sortBy, dateRange]);

  const loadBookings = async () => {
    setLoading(true);
    try {
      const [flightRes, hotelRes] = await Promise.allSettled([
        api.getFlightOrders(1, 50),
        api.getHotelOrders(1, 50),
      ]);

      const list: Booking[] = [];

      if (flightRes.status === "fulfilled" && Array.isArray(flightRes.value.flight_orders)) {
        for (const f of flightRes.value.flight_orders) {
          list.push({
            id: f.booking_id || f.flight_order_id || f.flight_order_db_id || 0,
            type: 'flight',
            status: f.status || 'pending',
            hotel: `Flight: ${f.departure_airport || '???'} → ${f.arrival_airport || '???'}`,
            pnr: f.pnr || null,
            userName: `${f.first_name || ''} ${f.last_name || ''}`.trim() || 'Unknown User',
            userEmail: f.email || null,
            amount: Number(f.total_price ?? 0),
            created: f.departure_time || f.created_at || new Date().toISOString(),
            origin: f.departure_airport,
            destination: f.arrival_airport,
            flightNumber: f.flight_number,
            airline: f.airline_name
          });
        }
      }

      if (hotelRes.status === "fulfilled" && Array.isArray(hotelRes.value.hotel_orders)) {
        for (const h of hotelRes.value.hotel_orders) {
          list.push({
            id: h.hotel_order_id || 0,
            type: 'hotel',
            status: h.status || 'pending',
            hotel: h.hotel_name || 'Hotel Booking',
            ref: h.reference_number || null,
            userName: `${h.first_name || ''} ${h.last_name || ''}`.trim() || 'Unknown User',
            userEmail: h.email || null,
            amount: Number(h.total_price ?? h.total_fare ?? 0),
            created: h.check_in_date || h.created_at || new Date().toISOString(),
            nights: h.nights || h.nights_count || 1,
            checkIn: h.check_in_date,
            checkOut: h.check_out_date,
            roomType: h.room_type,
            guests: h.guests || h.adults || 1
          });
        }
      }

      try {
        const invResp = await api.getInvoicesFromServer(1, 500);
        const invMap: Record<string, any> = {};
        if (invResp && Array.isArray(invResp.invoices)) {
          for (const inv of invResp.invoices) {
            const t = inv.type || inv['type'];
            const bid = inv.booking_id ?? inv.bookingId ?? inv['bookingId'] ?? inv.id ?? inv['order_id'] ?? null;
            if (t && bid) invMap[`${t}-${bid}`] = inv;
          }
        }
        setInvoiceMap(invMap);
      } catch (e) {
        console.warn('Could not load invoices list', e);
      }

      setBookings(list);
      setFilteredBookings(list);
    } catch (e: any) {
      setError(e?.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSortBookings = () => {
    if (!bookings) return;

    let filtered = [...bookings];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(b => 
        b.userName?.toLowerCase().includes(term) ||
        b.userEmail?.toLowerCase().includes(term) ||
        b.hotel?.toLowerCase().includes(term) ||
        b.pnr?.toLowerCase().includes(term) ||
        b.ref?.toLowerCase().includes(term) ||
        b.id.toString().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => 
        b.status?.toLowerCase() === statusFilter.toLowerCase()
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(b => b.type === typeFilter);
    }

    if (dateRange !== 'all') {
      const now = new Date();
      const rangeMap: Record<string, number> = {
        'today': 1,
        'week': 7,
        'month': 30,
        'quarter': 90
      };
      const days = rangeMap[dateRange];
      if (days) {
        const cutoff = new Date(now.setDate(now.getDate() - days));
        filtered = filtered.filter(b => new Date(b.created) >= cutoff);
      }
    }

    filtered.sort((a, b) => {
      switch(sortBy) {
        case 'created':
          return new Date(b.created).getTime() - new Date(a.created).getTime();
        case 'amount':
          return b.amount - a.amount;
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        case 'name':
          return (a.userName || '').localeCompare(b.userName || '');
        default:
          return 0;
      }
    });

    setFilteredBookings(filtered);
  };

  const handleView = async (b: Booking) => {
    try {
      if (b.type === 'flight') {
        const res = await api.getFlightOrderDetail(b.id);
        setModalData(res?.flight_order || res);
        setModalBooking(b);
        setModalTitle('Flight Booking Details');
        setModalOpen(true);
      } else if (b.type === 'hotel') {
        const res = await api.getHotelOrderDetail(b.id);
        setModalData(res?.hotel_order || res);
        setModalBooking(b);
        setModalTitle('Hotel Booking Details');
        setModalOpen(true);
      } else {
        alert('No detailed view available for this booking');
      }
    } catch (err) {
      console.warn('Could not fetch detail', err);
      alert('Failed to fetch booking details');
    }
  };

  const handleDownloadInvoice = async (b: Booking) => {
    try {
      if (!b.type) return alert('No invoice available for this booking');
      const { url, headers } = await api.downloadInvoice(b.type === 'flight' ? 'flight' : 'hotel', b.id);
      const resp = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `invoice_${b.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.warn('Download failed', err);
      alert('Failed to download invoice');
    }
  };

  const handleDownloadTicket = async (b: Booking) => {
    try {
      if (b.type !== 'flight') return alert('Ticket download supported for flights only');
      const idval = b.pnr || b.id;
      const { url, headers } = await api.downloadTicket('flight', idval as any);
      const resp = await axios.get(url, { headers, responseType: 'blob' });
      const blob = new Blob([resp.data], { type: resp.headers['content-type'] || 'application/pdf' });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = `ticket_${idval}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      console.warn('Ticket download failed', err);
      if (err?.response?.status === 404) alert('Ticket file not found (not generated)');
      else alert('Failed to download ticket');
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch(status?.toLowerCase()) {
      case 'confirmed':
      case 'completed':
      case 'issued':
        return 'badge-success';
      case 'pending':
      case 'processing':
        return 'badge-warning';
      case 'cancelled':
      case 'failed':
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  };

  const getTypeIcon = (type: string) => {
    switch(type) {
      case 'flight':
        return '✈️';
      case 'hotel':
        return '🏨';
      default:
        return '📦';
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
    setSortBy('created');
    setDateRange('all');
  };

  return (
    <div className="bookings-page">
      <div className="page-header">
        <div>
          <h1>Bookings</h1>
          <p>Manage and track all flight and hotel bookings</p>
        </div>
        <button className="btn-primary">
          <span>➕</span> New Booking
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name, email, PNR, ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-group">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
            <option value="completed">Completed</option>
          </select>

          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            <option value="flight">Flights Only</option>
            <option value="hotel">Hotels Only</option>
          </select>

          <select value={dateRange} onChange={(e) => setDateRange(e.target.value)}>
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">Last 90 Days</option>
          </select>

          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="created">Newest First</option>
            <option value="amount">Highest Amount</option>
            <option value="status">By Status</option>
            <option value="name">By Name</option>
          </select>
        </div>
      </div>

      <div className="stats-mini">
        <div className="stat-mini-item">
          <span className="stat-mini-label">Total</span>
          <span className="stat-mini-value">{filteredBookings?.length || 0}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Flights</span>
          <span className="stat-mini-value">{filteredBookings?.filter(b => b.type === 'flight').length || 0}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Hotels</span>
          <span className="stat-mini-value">{filteredBookings?.filter(b => b.type === 'hotel').length || 0}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Revenue</span>
          <span className="stat-mini-value">₹{filteredBookings?.reduce((sum, b) => sum + (b.amount || 0), 0).toLocaleString()}</span>
        </div>
        {(searchTerm || statusFilter !== 'all' || typeFilter !== 'all' || dateRange !== 'all') && (
          <button className="clear-filters" onClick={clearFilters}>
            Clear Filters ✕
          </button>
        )}
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading bookings...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={loadBookings} className="btn-outline">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="bookings-grid">
          {filteredBookings?.map((booking) => {
            const invoiceAvailable = invoiceMap[`${booking.type}-${booking.id}`];
            
            return (
              <div key={booking.id} className="booking-card">
                <div className="card-header">
                  <div className="booking-type">
                    <span className="type-icon">{getTypeIcon(booking.type || '')}</span>
                    <span className="type-label">{booking.type?.toUpperCase()}</span>
                    <span className="booking-id-label">#{booking.id}</span>
                  </div>
                  <span className={`status-badge ${getStatusBadgeClass(booking.status)}`}>
                    {booking.status}
                  </span>
                </div>

                <div className="card-body">
                  {booking.type === 'flight' && (
                    <div className="flight-details">
                      <div className="flight-route">
                        <div className="route-point">
                          <span className="point-code">{booking.origin || '???'}</span>
                          <span className="point-label">Departure</span>
                        </div>
                        <div className="route-arrow">→</div>
                        <div className="route-point">
                          <span className="point-code">{booking.destination || '???'}</span>
                          <span className="point-label">Arrival</span>
                        </div>
                      </div>
                      
                      {booking.flightNumber && (
                        <div className="flight-number">
                          Flight: {booking.flightNumber} {booking.airline && `(${booking.airline})`}
                        </div>
                      )}
                      
                      {booking.pnr && (
                        <div className="booking-ref">
                          <span className="ref-label">PNR:</span>
                          <span className="ref-value">{booking.pnr}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {booking.type === 'hotel' && (
                    <div className="hotel-details">
                      <div className="hotel-name">{booking.hotel}</div>
                      
                      {booking.roomType && (
                        <div className="hotel-room">Room: {booking.roomType}</div>
                      )}
                      
                      <div className="hotel-stay">
                        {booking.nights && (
                          <span className="hotel-nights">{booking.nights} night{booking.nights > 1 ? 's' : ''}</span>
                        )}
                        {booking.guests && (
                          <span className="hotel-guests"> • {booking.guests} guest{booking.guests > 1 ? 's' : ''}</span>
                        )}
                      </div>
                      
                      {booking.checkIn && booking.checkOut && (
                        <div className="hotel-dates">
                          {new Date(booking.checkIn).toLocaleDateString()} - {new Date(booking.checkOut).toLocaleDateString()}
                        </div>
                      )}
                      
                      {booking.ref && (
                        <div className="booking-ref">
                          <span className="ref-label">Ref:</span>
                          <span className="ref-value">{booking.ref}</span>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="booking-user">
                    <div className="user-avatar">
                      {booking.userName?.charAt(0) || 'U'}
                    </div>
                    <div className="user-info">
                      <div className="user-name">{booking.userName || 'Unknown User'}</div>
                      {booking.userEmail && (
                        <div className="user-email">{booking.userEmail}</div>
                      )}
                    </div>
                  </div>

                  <div className="booking-meta">
                    <div className="booking-date">
                      <span className="meta-icon">📅</span>
                      <span className="meta-text">
                        {new Date(booking.created).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                    <div className="booking-amount">
                      <span className="amount">₹{booking.amount?.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="card-footer">
                  <button className="btn-view" onClick={() => handleView(booking)}>
                    View Details
                  </button>
                  <div className="action-buttons">
                    {invoiceAvailable && (
                      <button 
                        className="btn-icon" 
                        onClick={() => handleDownloadInvoice(booking)}
                        title="Download Invoice"
                      >
                        📄
                      </button>
                    )}
                    {booking.type === 'flight' && (
                      <button 
                        className="btn-icon" 
                        onClick={() => handleDownloadTicket(booking)}
                        title="Download Ticket"
                      >
                        🎫
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {filteredBookings?.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">📭</span>
              <h3>No bookings found</h3>
              <p>Try adjusting your filters or search terms</p>
              <button onClick={clearFilters} className="btn-outline">
                Clear Filters
              </button>
            </div>
          )}
        </div>
      )}

      <DetailModal open={modalOpen} onClose={() => setModalOpen(false)} title={modalTitle}>
        {modalBooking && (
          <div className="modal-actions">
            {invoiceMap[`${modalBooking.type}-${modalBooking.id}`] && (
              <button 
                className="btn-outline-primary" 
                onClick={() => handleDownloadInvoice(modalBooking)}
              >
                Download Invoice
              </button>
            )}
            {modalBooking.type === 'flight' && (
              <button 
                className="btn-danger" 
                onClick={() => handleDownloadTicket(modalBooking)}
              >
                Download Ticket
              </button>
            )}
          </div>
        )}
        {modalData ? (
          <div className="modal-content">
            <div className="details-grid">
              {Object.entries(modalData).map(([key, value]) => (
                <div key={key} className="detail-row">
                  <div className="detail-key">{key}</div>
                  <div className="detail-value">
                    {typeof value === 'object' ? (
                      <pre>{JSON.stringify(value, null, 2)}</pre>
                    ) : (
                      String(value)
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="text-muted">No details available</div>
        )}
      </DetailModal>
    </div>
  );
};

export default Bookings;